import { useLocation, Link, Navigate } from "react-router-dom";
import { Globe, AppWindow, Palette, MessageSquare, Megaphone, Bot, CheckCircle2, ArrowRight } from "lucide-react";
import { Section, SectionHeader, PrimaryButton, SecondaryButton } from "@/components/Section";
import { ContactForm } from "@/components/ContactForm";
import { SocialPlanCard } from "@/components/SocialPlanCard";
import { SEO } from "@/components/SEO";
import { services } from "@/config/site";

const icons = { Globe, AppWindow, Palette, MessageSquare, Megaphone, Bot };

export default function ServicePage() {
    const { pathname } = useLocation();
    const slug = pathname.replace(/^\//, "").split("/")[0];
    const service = services.find((s) => s.slug === slug);
    if (!service) return <Navigate to="/uslugi" replace />;
    const Icon = icons[service.icon] || Globe;

    return (
        <>
            <SEO title={`${service.title} – Szymkas Agency`} description={service.short} />

            <section className="relative bg-[#06111F] text-white pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
                <div className="absolute inset-0 bg-grid-faint opacity-50" />
                <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full bg-[#14B8A6]/10 blur-3xl" />
                <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12 grid lg:grid-cols-5 gap-10 items-center">
                    <div className="lg:col-span-3">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-[#AAB6C5] mb-5">
                            <Icon className="w-4 h-4 text-[#14B8A6]" /> Usługa Szymkas Agency
                        </div>
                        <h1 className="font-display font-extrabold text-[38px] md:text-[58px] lg:text-[64px] leading-[1.05] tracking-tight">
                            {service.title}
                        </h1>
                        <p className="mt-5 text-[#AAB6C5] text-base md:text-lg max-w-2xl leading-relaxed">{service.short}</p>
                        <div className="mt-7 flex flex-col sm:flex-row gap-3">
                            <PrimaryButton to="/kontakt" testid="svc-hero-cta">Umów konsultację <ArrowRight className="w-4 h-4" /></PrimaryButton>
                            <SecondaryButton to="/pakiety" light testid="svc-hero-cta2">Zobacz pakiety</SecondaryButton>
                        </div>
                    </div>
                    <div className="lg:col-span-2 hidden lg:flex justify-center">
                        <div className="w-48 h-48 rounded-3xl bg-[#14B8A6]/10 border border-[#14B8A6]/30 flex items-center justify-center">
                            <Icon className="w-24 h-24 text-[#14B8A6]" />
                        </div>
                    </div>
                </div>
            </section>

            <Section variant="light">
                <div className="grid lg:grid-cols-2 gap-10 md:gap-14">
                    <div>
                        <div className="text-xs font-semibold tracking-widest uppercase text-[#14B8A6] mb-3">Problem klienta</div>
                        <h2 className="font-display font-bold text-[30px] md:text-[40px] leading-tight">
                            Co typowo blokuje firmy?
                        </h2>
                        <p className="mt-5 text-[#0B1220]/75 text-base md:text-lg leading-relaxed">{service.problem}</p>
                    </div>
                    <div>
                        <div className="text-xs font-semibold tracking-widest uppercase text-[#14B8A6] mb-3">Korzyści</div>
                        <h2 className="font-display font-bold text-[30px] md:text-[40px] leading-tight">Co zyskasz?</h2>
                        <ul className="mt-5 space-y-3">
                            {service.benefits.map((b) => (
                                <li key={b} className="flex gap-3 text-[#0B1220]/85 text-base">
                                    <CheckCircle2 className="w-5 h-5 text-[#14B8A6] flex-shrink-0 mt-0.5" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Section>

            <Section variant="gray">
                <SectionHeader eyebrow="Zakres usługi" title="Co wchodzi w realizację" />
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                    {service.scope.map((item, i) => (
                        <div key={item} className="rounded-[16px] bg-white border border-[#E5E7EB] p-5 flex items-start gap-3" data-testid={`svc-scope-${i}`}>
                            <span className="w-7 h-7 rounded-full bg-[#14B8A6]/10 text-[#14B8A6] font-bold text-sm flex items-center justify-center flex-shrink-0">
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <span className="text-[#0B1220] text-[15px] leading-snug">{item}</span>
                        </div>
                    ))}
                </div>
                {service.slug === "social-media" && <SocialPlanCard testidPrefix="svc-social-plan" />}
            </Section>

            <Section variant="light">
                <div className="grid lg:grid-cols-5 gap-10 items-start">
                    <div className="lg:col-span-2">
                        <div className="text-xs font-semibold tracking-widest uppercase text-[#14B8A6] mb-3">Zacznijmy</div>
                        <h2 className="font-display font-bold text-[30px] md:text-[40px] leading-tight">
                            Opowiedz nam o projekcie
                        </h2>
                        <p className="mt-4 text-[#0B1220]/70 text-base md:text-lg leading-relaxed">
                            Wypełnij krótki formularz – odezwiemy się, aby ustalić zakres i przygotować wycenę dopasowaną do Twoich potrzeb.
                        </p>
                        <div className="mt-6 space-y-2 text-sm text-[#0B1220]/80">
                            <div>📞 <a href="tel:+48733934001" className="font-semibold hover:text-[#14B8A6]">733 934 001</a></div>
                            <div>✉ <a href="mailto:szymkas.agency@gmail.com" className="font-semibold hover:text-[#14B8A6]">szymkas.agency@gmail.com</a></div>
                        </div>
                    </div>
                    <div className="lg:col-span-3">
                        <ContactForm testidPrefix={`svc-${service.slug}`} />
                    </div>
                </div>
            </Section>

            <Section variant="gray">
                <div className="grid sm:grid-cols-3 gap-4">
                    {services.filter((s) => s.slug !== service.slug).slice(0, 3).map((s) => (
                        <Link key={s.slug} to={`/${s.slug}`} className="rounded-[16px] bg-white border border-[#E5E7EB] p-5 hover:border-[#14B8A6]/40 transition-colors" data-testid={`svc-related-${s.slug}`}>
                            <div className="text-xs text-[#14B8A6] font-semibold uppercase tracking-wider mb-1">Zobacz też</div>
                            <div className="font-display font-semibold text-[#0B1220]">{s.title}</div>
                        </Link>
                    ))}
                </div>
            </Section>
        </>
    );
}
